import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { portalCommands } from 'src/app/utils/portal-path';
import { UsersService } from 'src/app/services/users.service';
import { PermissionService } from 'src/app/services/permission.service';
import type { StoredSidebarAccess } from 'src/app/constants/sidebar-access';

export interface User {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  role: {
    id: string;
    name: string;
  };
  classes?: { id: string; name: string; major?: { id: string; name: string } | null }[];
  subjects?: { id: string; name: string }[];
  socketId?: string;
  created_on: string;
  updated_on: string;
  isDefault: boolean;
  isSystemUser?: boolean;
  sidebarAccess?: StoredSidebarAccess;
}

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.css']
})
export class UsersComponent {
  showDeleteModal = false;
  deleteModalTitle = '';
  deleteModalBody = '';
  deleteModalParams: Record<string, unknown> = {};
  open = false;
  mode = 'add';
  selected: User[] = [];
  selectedIdForEdit = '';
  users: User[] = [];
  filteredUsers: User[] = [];
  searchTerm = '';
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [10, 20, 50, 100];
  isError = false;
  errorMessage = '';

  constructor(
    public usersService: UsersService,
    public permissions: PermissionService,
    private router: Router
  ) {}

  async ngOnInit() {
    await this.fetchUsers();
  }

  async fetchUsers() {
    this.users = await this.usersService.getUsers();
    this.applyFilter();
  }

  get paginatedUsers(): User[] {
    const start = this.pageIndex * this.pageSize;
    return this.filteredUsers.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredUsers.length / this.pageSize));
  }

  get pageStart(): number {
    return this.filteredUsers.length ? this.pageIndex * this.pageSize + 1 : 0;
  }

  get pageEnd(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.filteredUsers.length);
  }

  applyFilter() {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredUsers = term
      ? this.users.filter(user =>
          user.username.toLowerCase().includes(term) ||
          user.firstName?.toLowerCase().includes(term) ||
          user.lastName?.toLowerCase().includes(term) ||
          user.role?.name?.toLowerCase().includes(term)
        )
      : [...this.users];
    this.pageIndex = 0;
  }

  onSearchChange() {
    this.applyFilter();
  }

  onPageSizeChange(size: string) {
    this.pageSize = Number(size);
    this.pageIndex = 0;
  }

  previousPage() {
    if (this.pageIndex > 0) {
      this.pageIndex--;
    }
  }

  nextPage() {
    if (this.pageIndex < this.totalPages - 1) {
      this.pageIndex++;
    }
  }

  isSelected(user: User): boolean {
    return this.selected.some(item => item.id === user.id);
  }

  toggleSelection(user: User, event: Event) {
    event.stopPropagation();
    if (this.isSelected(user)) {
      this.selected = this.selected.filter(item => item.id !== user.id);
    } else {
      this.selected = [...this.selected, user];
    }
  }

  toggleSelectAll(event: Event) {
    event.stopPropagation();
    const pageIds = new Set(this.paginatedUsers.map(user => user.id));
    const allPageSelected = this.paginatedUsers.every(user => this.isSelected(user));
    if (allPageSelected) {
      this.selected = this.selected.filter(user => !pageIds.has(user.id));
    } else {
      const merged = [...this.selected];
      for (const user of this.paginatedUsers) {
        if (!merged.some(item => item.id === user.id)) {
          merged.push(user);
        }
      }
      this.selected = merged;
    }
  }

  isAllPageSelected(): boolean {
    return this.paginatedUsers.length > 0 && this.paginatedUsers.every(user => this.isSelected(user));
  }

  clearSelection() {
    this.selected = [];
  }

  openDetails(user: User) {
    this.router.navigate(portalCommands(this.router, 'settings', 'users', user.id));
  }

  onEdit() {
    this.mode = 'edit';
    this.selectedIdForEdit = this.selected[0].id;
    this.open = true;
  }

  onAdd() {
    this.mode = 'add';
    this.open = true;
  }

  async handleUserUpdated() {
    await this.fetchUsers();
    this.selected = [];
  }

  onDeleteMany() {
    this.isError = false;
    this.showDeleteModal = true;
    this.deleteModalTitle = 'COMMON.CONFIRM_DELETE';
    if (this.selected.length === this.users.length) {
      this.deleteModalBody = 'USERS.DELETE_ALL';
      this.deleteModalParams = {};
    } else if (this.selected.length > 1) {
      this.deleteModalBody = 'USERS.DELETE_MANY';
      this.deleteModalParams = { count: this.selected.length };
    } else {
      this.deleteModalBody = 'USERS.DELETE_ONE';
      this.deleteModalParams = { name: this.selected[0].username };
    }
  }

  async onDeleteModalClose(confirmed: boolean) {
    if (confirmed) {
      for (const user of this.selected) {
        try {
          await this.usersService.deleteUser(user.id);
        } catch (error: any) {
          const message = error?.error?.errorMessages?.[0]?.message || error?.message || 'USERS.DELETE_FAILED';
          this.displayError(message);
          await this.fetchUsers();
          return;
        }
      }
      this.selected = [];
      await this.fetchUsers();
    }
    this.showDeleteModal = false;
  }

  displayError(errorMessage: string) {
    this.isError = true;
    this.errorMessage = errorMessage;
  }

  formatAssignments(items?: { name: string }[]): string {
    return items?.length ? items.map(item => item.name).join(', ') : '—';
  }
}
